'use client';

import type { SupportedLanguages } from 'i18n-settings';
import { useMemo } from 'react';

import { DEFAULT_SORT_FIELD, DEFAULT_SORT_ORDER, MAX_PAGE_SIZE } from '@/constants/variables';
import type { QuizListItem, QuizListQueryParams, QuizListServiceConfig } from '@/services/httpServices/QuizServices/types';
import type { SequenceListItem, SequenceListQueryParams, SequenceListServiceConfig } from '@/services/httpServices/SequenceServices/types';
import type { FormOption } from '@/types/general';
import type { InfiniteUnitData, PageData } from '@/types/infinite-query';
import type { ContentUnitType } from '@/types/unit';
import { getCurrentLocaleClient, getValidatedLanguageOrFallback } from '@/utils/language-helpers';

import useCombineInfiniteQueryData from '../utils/useCombineInfiniteQueryData';
import useInfiniteScroll from '../utils/useInfiniteScroll';
import { useQueryConfig } from './useQueryConfig';
import { useInfiniteQuizQuery } from './useQuizQuery';
import { useInfiniteSequenceQuery } from './useSequenceQuery';

/**
 * Initial item structure for prepending existing selections
 * Uses Pick to derive from existing list item types
 */
type InitialItem = Pick<SequenceListItem, 'id' | 'title'>;

/**
 * Return type for the infinite available units hook
 */
type InfiniteAvailableUnitsReturn = {
  sequences: InfiniteUnitData<SequenceListItem, FormOption>;
  quizzes: InfiniteUnitData<QuizListItem, FormOption>;
  isLoadingAny: boolean;
  isFetchingNextPageAny: boolean;
  hasErrors: boolean;
};

/**
 * Configuration map for content unit types
 * Enum-based pattern for type-safe unit configuration
 */
const CONTENT_UNIT_CONFIG: Record<ContentUnitType, {
  createDefaultItem: (id: string, title: string, language: SupportedLanguages) => SequenceListItem | QuizListItem;
}> = {
  sequence: {
    createDefaultItem: (id, title, language): SequenceListItem => ({
      id,
      title,
      language,
      status: 'published',
      numberOfSteps: 0,
      coverImage: null,
      permissions: {
        delete: { enabled: false, disabledReasons: [] },
      },
      linkedCourses: [],
      setLanguages: [language],
      missingLanguages: [],
    }),
  },
  quiz: {
    createDefaultItem: (id, title, language): QuizListItem => ({
      id,
      title,
      permissions: {
        delete: { enabled: false, disabledReasons: [] },
      },
      linkedCourses: [],
      setLanguages: [language],
      missingLanguages: [],
    }),
  },
};

/**
 * Deduplicates an array of items by their id property
 * Preserves the first occurrence of each id
 */
function deduplicateById<TNode extends { id: string }>(nodes: TNode[]): TNode[] {
  const seenIds = new Set<string>();
  return nodes.filter((node) => {
    if (seenIds.has(node.id) === true) {
      return false;
    }
    seenIds.add(node.id);
    return true;
  });
}

/**
 * Generic combiner function for infinite query pages
 * Combines all nodes from pages while preserving the latest pageInfo
 * Deduplicates nodes by id to prevent duplicate key errors
 */
function combinePages<TNode extends { id: string }>(
  pages: Array<PageData<TNode>>,
): PageData<TNode> | undefined {
  if (pages.length === 0) {
    return undefined;
  }

  const lastPage = pages[pages.length - 1];
  if (lastPage === undefined) {
    return undefined;
  }

  const allNodes = pages.flatMap(page => page.nodes);
  const uniqueNodes = deduplicateById(allNodes);

  return {
    nodes: uniqueNodes,
    pageInfo: lastPage.pageInfo,
  };
}

/**
 * Creates base query params shared between sequence and quiz list queries
 */
function createBaseQueryParams(
  searchTerm: string,
  language: SupportedLanguages,
): Omit<SequenceListQueryParams, 'filters'> & { filters: string } {
  return {
    paginationFirst: MAX_PAGE_SIZE,
    paginationAfter: '',
    paginationLast: undefined,
    paginationBefore: undefined,
    sortByField: DEFAULT_SORT_FIELD,
    sortByOrder: DEFAULT_SORT_ORDER,
    searchTerm,
    language,
    searchByType: 'title',
    filters: '',
  };
}

/**
 * Prepends initial sequence items to the list if not already present
 * Handles multiple items and deduplicates
 */
function prependInitialSequences(
  nodes: SequenceListItem[],
  initialItems: InitialItem[],
  language: SupportedLanguages,
): SequenceListItem[] {
  if (initialItems.length === 0) {
    return nodes;
  }

  const existingIds = new Set(nodes.map(node => node.id));
  const itemsToAdd: SequenceListItem[] = [];

  for (const item of initialItems) {
    if (item.id !== '' && existingIds.has(item.id) === false) {
      const defaultItem = CONTENT_UNIT_CONFIG.sequence.createDefaultItem(
        item.id,
        item.title,
        language,
      );
      itemsToAdd.push(defaultItem as SequenceListItem);
      existingIds.add(item.id); // Prevent duplicates within initialItems
    }
  }

  if (itemsToAdd.length === 0) {
    return nodes;
  }

  return [...itemsToAdd, ...nodes];
}

/**
 * Prepends initial quiz items to the list if not already present
 * Handles multiple items and deduplicates
 */
function prependInitialQuizzes(
  nodes: QuizListItem[],
  initialItems: InitialItem[],
  language: SupportedLanguages,
): QuizListItem[] {
  if (initialItems.length === 0) {
    return nodes;
  }

  const existingIds = new Set(nodes.map(node => node.id));
  const itemsToAdd: QuizListItem[] = [];

  for (const item of initialItems) {
    if (item.id !== '' && existingIds.has(item.id) === false) {
      const defaultItem = CONTENT_UNIT_CONFIG.quiz.createDefaultItem(
        item.id,
        item.title,
        language,
      );
      itemsToAdd.push(defaultItem as QuizListItem);
      existingIds.add(item.id); // Prevent duplicates within initialItems
    }
  }

  if (itemsToAdd.length === 0) {
    return nodes;
  }

  return [...itemsToAdd, ...nodes];
}

/**
 * Converts list items to form options with deduplication by ID
 */
function toFormOptions<TNode extends { id: string; title: string }>(
  nodes: TNode[],
): FormOption[] {
  const seenIds = new Set<string>();
  const options: FormOption[] = [];

  for (const node of nodes) {
    if (seenIds.has(node.id) === false) {
      seenIds.add(node.id);
      options.push({
        value: node.id,
        label: node.title,
      });
    }
  }

  return options;
}

/**
 * Hook to fetch available sequences and quizzes with infinite scrolling
 * @param isDisabled - Whether to disable the queries
 * @param language - Optional language override for fetching content
 * @param initialSequenceItems - Array of initial sequence items to prepend (from course data)
 * @param initialQuizItems - Array of initial quiz items to prepend (from course data)
 * @returns Object containing sequences and quizzes data with infinite scroll support
 */
export function useInfiniteAvailableUnits(
  isDisabled: boolean,
  language?: string,
  initialSequenceItems: InitialItem[] = [],
  initialQuizItems: InitialItem[] = [],
): InfiniteAvailableUnitsReturn {
  const { httpRequestConfig, isEnabled: isSpaceReady } = useQueryConfig();
  const userLocale = useMemo(() => getCurrentLocaleClient(), []);
  const effectiveLanguage = useMemo(
    () => getValidatedLanguageOrFallback(language !== undefined && language !== '' ? language : userLocale),
    [language, userLocale],
  );

  const baseQueryParams = useMemo(
    () => createBaseQueryParams('', effectiveLanguage),
    [effectiveLanguage],
  );

  const sequenceParams = useMemo<SequenceListServiceConfig>(() => ({
    httpRequestConfig,
    params: {
      query: baseQueryParams,
    },
  }), [httpRequestConfig, baseQueryParams]);

  const quizParams = useMemo<QuizListServiceConfig>(() => ({
    httpRequestConfig,
    params: {
      query: baseQueryParams as QuizListQueryParams,
    },
  }), [httpRequestConfig, baseQueryParams]);

  const isQueryEnabled = isDisabled === false && isSpaceReady === true;

  const sequenceInfiniteQuery = useInfiniteSequenceQuery(sequenceParams, isQueryEnabled);
  const quizInfiniteQuery = useInfiniteQuizQuery(quizParams, isQueryEnabled);

  const sequenceScrollResult = useInfiniteScroll(sequenceInfiniteQuery);
  const quizScrollResult = useInfiniteScroll(quizInfiniteQuery);

  const combinedSequenceData = useCombineInfiniteQueryData(
    sequenceInfiniteQuery.data,
    combinePages<SequenceListItem>,
  );

  const combinedQuizData = useCombineInfiniteQueryData(
    quizInfiniteQuery.data,
    combinePages<QuizListItem>,
  );

  return useMemo(() => {
    const sequenceNodes = combinedSequenceData?.nodes ?? [];
    const quizNodes = combinedQuizData?.nodes ?? [];

    const finalSequenceNodes = prependInitialSequences(
      sequenceNodes,
      initialSequenceItems,
      effectiveLanguage,
    );

    const finalQuizNodes = prependInitialQuizzes(
      quizNodes,
      initialQuizItems,
      effectiveLanguage,
    );

    return {
      sequences: {
        isLoading: sequenceInfiniteQuery.isLoading,
        isInitialLoading: sequenceInfiniteQuery.isLoading,
        isFetchingNextPage: sequenceInfiniteQuery.isFetchingNextPage,
        isError: sequenceInfiniteQuery.isError,
        error: sequenceInfiniteQuery.error,
        options: toFormOptions(finalSequenceNodes),
        data: finalSequenceNodes,
        hasNextPage: sequenceScrollResult.hasNextPage ?? false,
        loadMoreRef: sequenceScrollResult.loadMoreRef,
        refetch: sequenceInfiniteQuery.refetch,
      },
      quizzes: {
        isLoading: quizInfiniteQuery.isLoading,
        isInitialLoading: quizInfiniteQuery.isLoading,
        isFetchingNextPage: quizInfiniteQuery.isFetchingNextPage,
        isError: quizInfiniteQuery.isError,
        error: quizInfiniteQuery.error,
        options: toFormOptions(finalQuizNodes),
        data: finalQuizNodes,
        hasNextPage: quizScrollResult.hasNextPage ?? false,
        loadMoreRef: quizScrollResult.loadMoreRef,
        refetch: quizInfiniteQuery.refetch,
      },
      isLoadingAny: sequenceInfiniteQuery.isLoading || quizInfiniteQuery.isLoading,
      isFetchingNextPageAny: sequenceInfiniteQuery.isFetchingNextPage || quizInfiniteQuery.isFetchingNextPage,
      hasErrors: sequenceInfiniteQuery.isError || quizInfiniteQuery.isError,
    };
  }, [
    combinedSequenceData,
    combinedQuizData,
    sequenceInfiniteQuery,
    quizInfiniteQuery,
    sequenceScrollResult,
    quizScrollResult,
    initialSequenceItems,
    initialQuizItems,
    effectiveLanguage,
  ]);
}
